import { useEffect, useState, ReactNode } from "react";
import { useDropzone, FileRejection, FileError, Accept } from 'react-dropzone';
import { Chip, Grid, Paper, styled } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import theme from "../Theme";

const FileChip = styled(Chip)((props) => ({
    backgroundColor: theme.palette.toast.main,
    borderColor: theme.palette.toast.contrastText,
    borderWidth: '1px',
    borderStyle: 'solid',
    color: theme.palette.toast.contrastText,
    marginRight: 5,
    marginBottom: 5,
}));

const RejectionChip = styled(Chip)((props) => ({
    backgroundColor: theme.palette.toastError.light,
    borderColor: theme.palette.toastError.main,
    color: theme.palette.toastError.main,
    borderWidth: '1px',
    borderStyle: 'solid',
    marginRight: 5,
    marginBottom: 5,
    justifyContent: 'space-between',
    
}));

const RejectionChipText = styled('div')((props) => ({
    color: theme.palette.error.main,
    fontWeight: 'bold',
    fontSize: '0.85em',
}));

const ChipPaper = styled(Paper)((props) => ({
    display: 'flex',
    justifyContent: 'start',
    flexWrap: 'wrap',
    listStyle: 'none',
}));

const DropZoneOverlayText = styled('div')((props) => ({
    padding: '0px 24px',
    textAlign: 'center',
    alignContent: 'center',
}));

const BrowseText = styled('span')((props) => ({
    color: theme.palette.primary.main,
}));

const UploadCloud = styled(CloudUploadIcon)((props) => ({
    color: theme.palette.primary.main,
    width: '100%',
    fontSize: 60,
}));

const LabelSection = styled(Grid)((props) => ({
    minHeight: 'unset',
    marginLeft: 16,
    color: theme.palette.grayscale.dark,
    paddingLeft: '0px !important',
    paddingTop: '16px !important',
    paddingBottom: '4px !important',
}));

const DropZoneLabel = styled('div')<{hasError?: boolean}>((props) => ({
    color: props.hasError ? theme.palette.error.main : theme.palette.grayscale.dark,
    fontSize: '16px'
}));

interface dropSectionProps {
    disabled?: boolean;
    children: Element | Element[] | ReactNode;
}

const DropSection = styled(Grid)<dropSectionProps>((props) => ({
    minHeight: 'unset',
    border: '1px dashed #00000099',
    borderRadius: 4,
    backgroundColor: '#FAFAFF',
    marginLeft: 16,
    paddingLeft: '0px !important',
    paddingTop: '16px !important',
    paddingBottom: '16px !important',
    cursor: 'pointer',
    ...(props.disabled === true && {
        cursor: 'default',
        backgroundColor: theme.palette.grayscale.light,
        opacity: '30%',
    }),
}));

interface FileDropZoneProps {
    maxNumberFiles: number;
    maxFileSize: number;
    acceptedFileTypes?: Accept;
    dropInstructionText?: string;
    label?: string;
    multipleFileSelection?: boolean;
    shouldClearData?: boolean;
    clearRejectionsOnDrop?: boolean;
    existingFiles?: File[];
    customValidator?: (file: File) => FileError[] | null;
    onChange: (acceptedFilesToUpload: File[]) => void;
}

const FileDropZone: React.FC<FileDropZoneProps> = props => {
    const { label, maxNumberFiles, maxFileSize, acceptedFileTypes, existingFiles, shouldClearData = false, clearRejectionsOnDrop = false, multipleFileSelection = false, dropInstructionText = "Drop your file here", customValidator } = props;
    const [filesToUpload, setFilesToUpload] = useState<File[]>(existingFiles ?? []);
    const [filesToReject, setFilesToReject] = useState<FileRejection[]>([]);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [firstLoad, setFirstLoad] = useState<boolean>(true);
    
    const { getRootProps, getInputProps, acceptedFiles, fileRejections } = useDropzone({
        maxSize: maxFileSize,
        multiple: multipleFileSelection,
        accept: acceptedFileTypes,
        validator: customValidator ?? undefined,
        disabled: isDisabled
    });

    const setDisabledAsNeeded = (currentUploadFiles: File[], currentRejectFiles: FileRejection[] = filesToReject) => {
        if (maxNumberFiles > 0) {
            const totalFiles = currentUploadFiles.length + currentRejectFiles.length;
            setIsDisabled(totalFiles >= maxNumberFiles);
        } else {
            setIsDisabled(false);
        }
    };

    
    useEffect(() => {
        //check disable initially based on existingFiles that there might be
        setDisabledAsNeeded(existingFiles ?? []);
    }, []);

    useEffect(() => {
        if (existingFiles && !acceptedFiles.length && firstLoad) {
            setFilesToUpload(existingFiles);
            setFirstLoad(false);
            setDisabledAsNeeded(existingFiles);
        }
    }, [existingFiles, acceptedFiles]);

    useEffect(() => {
        if (shouldClearData === true) {
            setToDefaults();
        }
    }, [shouldClearData]);

    useEffect(() => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            // clear any previous rejection chips when a new file is dropped
            if (clearRejectionsOnDrop && filesToReject.length > 0) {
                setFilesToReject([]);
            }

            // purge any duplicate named files that might already be in filesToUpload
            let filteredAcceptedFiles = filterOutDuplicateFiles(acceptedFiles);
            
            // Handle maxNumberFiles limit manually
            let filesToAccept: File[] = [];
            let filesToRejectDueToLimit: File[] = [];
            
            if (maxNumberFiles > 0) {
                const currentFileCount = filesToUpload.length;
                const availableSlots = maxNumberFiles - currentFileCount;
                
                if (availableSlots > 0) {
                    // Accept files up to the available slots
                    filesToAccept = filteredAcceptedFiles.slice(0, availableSlots);
                    // Reject any excess files
                    filesToRejectDueToLimit = filteredAcceptedFiles.slice(availableSlots);
                } else {
                    // No slots available, reject all files
                    filesToRejectDueToLimit = filteredAcceptedFiles;
                }
            } else {
                // No limit, accept all files
                filesToAccept = filteredAcceptedFiles;
            }
            
            // Add rejected files due to limit to rejection list
            if (filesToRejectDueToLimit.length > 0) {
                setFilesAsRejectedDueToLimit(filesToRejectDueToLimit);
            }
            
            let newUploadArr = filesToUpload;
            // Add accepted files to upload list
            if (filesToAccept.length > 0) {
                newUploadArr = filesToUpload.concat(filesToAccept);
                setFilesToUpload(newUploadArr);
                props.onChange(newUploadArr);
            }
            
            // Update disabled state based on total files
            let newRejectArr = filesToReject;
            if (filesToRejectDueToLimit.length > 0) {
                let rejections = filesToRejectDueToLimit?.map((file) => {
                    let error: FileError = { message: "File limit exceeded.", code: "LIMIT" };
                    return {
                        file: file,
                        errors: [error]
                    } as FileRejection;
                }) as FileRejection[];
                newRejectArr = filesToReject.concat(rejections);
            }
            
            setDisabledAsNeeded(newUploadArr, newRejectArr);
        }
        
    }, [acceptedFiles]);

    useEffect(() => {
        if (fileRejections && fileRejections.length > 0) {
            // clear any previous rejection chips when a new file is dropped, then add new rejections
            let baseRejectArr = (clearRejectionsOnDrop) ? [] : filesToReject;
            let newArr = baseRejectArr.concat(fileRejections);
            setFilesToReject(newArr);
        }
    }, [fileRejections]);

    const setToDefaults = () => {
        setFilesToUpload([]);
        setFilesToReject([]);
    };

    

    const filterOutDuplicateFiles = (acceptedFiles: File[]) => {
        // purge any duplicate named files and move the duplicates 
        // to filesToReject to display an error message as to why it wasn't added
        let existingFileNames: string[] = [];
        filesToUpload.forEach(f => existingFileNames.push(f.name));

        let fileDuplicates = acceptedFiles.filter(f => existingFileNames.indexOf(f.name) >= 0);
        if (fileDuplicates.length > 0) {
            setDuplicateFilesAsRejected(fileDuplicates);
            // return filtered files without duplicates
            return acceptedFiles.filter(f => fileDuplicates.indexOf(f) < 0);
        }
        // no duplicates so return all files
        return acceptedFiles;
    };

    const setDuplicateFilesAsRejected = (duplicateFiles: File[]) => {
        if (duplicateFiles && duplicateFiles.length > 0) {
            let rejections = duplicateFiles?.map((file) => {
                let error: FileError = { message: "Duplicate file name.", code: "DUP" };
                return {
                    file: file,
                    errors: [error]
                } as FileRejection;
            }) as FileRejection[];

            let newArray = filesToReject.concat(rejections);
            setFilesToReject(newArray);
        }
    };

    const setFilesAsRejectedDueToLimit = (limitExceededFiles: File[]) => {
        if (limitExceededFiles && limitExceededFiles.length > 0) {
            let rejections = limitExceededFiles?.map((file) => {
                let error: FileError = { message: "File limit exceeded.", code: "LIMIT" };
                return {
                    file: file,
                    errors: [error]
                } as FileRejection;
            }) as FileRejection[];

            let newArray = filesToReject.concat(rejections);
            setFilesToReject(newArray);
        }
    };

    const promoteRejectedFilesIfSlotAvailable = (currentUploadFiles: File[], currentRejectFiles: FileRejection[]) => {
        if (maxNumberFiles <= 0) return { newUploadFiles: currentUploadFiles, newRejectFiles: currentRejectFiles };

        // Check if we have available slots
        const availableSlots = maxNumberFiles - currentUploadFiles.length;
        if (availableSlots <= 0) return { newUploadFiles: currentUploadFiles, newRejectFiles: currentRejectFiles };

        // Find the first file rejected due to limit
        const firstLimitRejectedFile = currentRejectFiles.find(fr => 
            fr.errors.some(err => err.code === "LIMIT")
        );

        if (!firstLimitRejectedFile) return { newUploadFiles: currentUploadFiles, newRejectFiles: currentRejectFiles };

        // Promote the first limit-rejected file to accepted
        const newUploadFiles = [...currentUploadFiles, firstLimitRejectedFile.file];
        const newRejectFiles = currentRejectFiles.filter(fr => fr.file.name !== firstLimitRejectedFile.file.name);

        return { newUploadFiles, newRejectFiles };
    };
    
    const onFileChipDelete = (file: File) => {
        if (file && filesToUpload) {
            // Remove file from upload list
            let newUploadArray = filesToUpload.filter((x) => x.name !== file.name);
            
            // Check if we can promote a rejected file
            const { newUploadFiles, newRejectFiles } = promoteRejectedFilesIfSlotAvailable(newUploadArray, filesToReject);
            
            // Update states
            setFilesToUpload(newUploadFiles);
            setFilesToReject(newRejectFiles);
            setDisabledAsNeeded(newUploadFiles, newRejectFiles);
            props.onChange(newUploadFiles);
        }
    };

    const onFileRejectChipDelete = (fileReject: FileRejection) => {
        if (fileReject && filesToReject) {
            // Remove file from reject list
            let newRejectArray = filesToReject.filter((x) => x.file.name !== fileReject.file.name);
            
            // Update states
            setFilesToReject(newRejectArray);
            setDisabledAsNeeded(filesToUpload, newRejectArray);
        }
    };

    const getFileChips = () => {
        if (filesToUpload && filesToUpload.length) {
            return (
                <ChipPaper elevation={0} >
                    {filesToUpload.map((file: File) => (
                        <Grid key={file.name}>
                            <FileChip label={file.name} onDelete={() => onFileChipDelete(file)} />
                        </Grid>
                    ))}
                </ChipPaper>
            );
        }
        return null;
    };
    
    const getFileRejectionChips = () => {
        return (
            <Paper elevation={0} >
                {filesToReject.map((fileReject: FileRejection) => (
                    <Grid key={fileReject.file.name}>
                        <RejectionChip
                            label={<div><RejectionChipText>{`${fileReject.file.name}: ${fileReject.errors[0].message}`}</RejectionChipText></div>}
                            onDelete={() => onFileRejectChipDelete(fileReject)}
                        />
                    </Grid>
                ))}
            </Paper>
        );
    };

    
    return (
        <Grid container spacing={2}>
            { label && label.length > 0 && 
                <LabelSection>
                    <DropZoneLabel hasError={filesToReject.length > 0}>{label}</DropZoneLabel>
                </LabelSection>   
            }
            <DropSection item xs={12} aria-label='File-Upload' disabled={isDisabled}>
                <div {...getRootProps()}>
                    <input data-cy="file-upload-input" {...getInputProps()} />
                    <div>
                        <UploadCloud fontSize={'large'} />
                        <div>
                            <DropZoneOverlayText>
                                <div>
                                    <span >
                                        {dropInstructionText} or <BrowseText>Browse</BrowseText>
                                    </span>
                                </div>
                            </DropZoneOverlayText>
                        </div>
                    </div>
                </div>
            </DropSection>
            <Grid item xs={12}>
                {getFileChips()}
            </Grid>
            <Grid item xs={12}>
                {getFileRejectionChips()}
            </Grid>
        </Grid >
    );
};

export default FileDropZone;
